// Layout · Inline
//
// Flex-row container. Closed-enum `gap` over the shared spacing scale.
// No padding, no border, no `style`. Wrap in `Box` when a surface is needed.

import type { ReactNode } from "react";
import { GAP_TO_SPACE, type Gap } from "@ds/layout/Stack.helpers";

export type { Gap };

export type InlineAlign   = "start" | "center" | "end" | "baseline" | "stretch";
export type InlineJustify = "start" | "center" | "end" | "between" | "around" | "evenly";

export interface InlineInput {
  children: ReactNode;
  /** Gap between children. Default `"sm"`. */
  gap?: Gap;
  /** Cross-axis (vertical) alignment. Default `"center"`. */
  align?: InlineAlign;
  /** Main-axis (horizontal) alignment. */
  justify?: InlineJustify;
  /** Allow items to wrap. */
  wrap?: boolean;
  /** Render as `inline-flex`. */
  inline?: boolean;
  fullWidth?: boolean;
  /** Stretch to the parent's full height (`height: 100%`). Use to propagate a
   *  bounded height down to scrollable children inside a flex-column parent. */
  fullHeight?: boolean;
  /** Root element. Default `"div"`. Use `"span"` for inline-flow contexts. */
  as?: "div" | "span";
  testId?: string;
  className?: string;
}

export type InlineProps = InlineInput;

function alignToCss(a: InlineAlign): string {
  if (a === "start") return "flex-start";
  if (a === "end")   return "flex-end";
  return a;
}

function justifyToCss(j: InlineJustify): string {
  if (j === "start")   return "flex-start";
  if (j === "end")     return "flex-end";
  if (j === "between") return "space-between";
  if (j === "around")  return "space-around";
  if (j === "evenly")  return "space-evenly";
  return j;
}

export function Inline({
  children,
  gap = "sm",
  align = "center",
  justify,
  wrap = false,
  inline = false,
  fullWidth,
  fullHeight,
  as = "div",
  testId,
  className,
}: InlineProps) {
  const style: Record<string, unknown> = {
    display: inline ? "inline-flex" : "flex",
    flexDirection: "row",
    gap: GAP_TO_SPACE[gap],
    alignItems: alignToCss(align),
    minWidth: 0,
    flexWrap: wrap ? "wrap" : "nowrap",
  };
  if (justify)    style.justifyContent = justifyToCss(justify);
  if (fullWidth)  style.width          = "100%";
  if (fullHeight) { style.height = "100%"; style.minHeight = 0; }

  const Tag = as;
  return (
    <Tag
      data-testid={testId}
      className={className}
      style={style}
    >
      {children}
    </Tag>
  );
}
